import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

export const generateUUID = () => {
  return uuidv4();
};

export const replaceValueInDataSet = (val = "", isAmount = false) => {
  if (!val) return "-";
  let value = val.toLowerCase();
  let amount = isAmount ? <b>USD</b> : "";
  if (value == "na" || value == "n/a" || value == "") {
    return "-";
  }
  return val.replaceAll(/\u2010|\u2011|\u2012|\u2013|\u2014|\u2015/g, "-") + amount;
};
export const convertSeconds = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);

  return { hours, minutes, seconds };
};

export const generateChartConfig = ({
  categories,
  isDay,
  totalUsers = 0,
  color = "#cdbef9",
  type = "line",
  suffix = "",
}) => {
  const chartOptions = {
    chart: {
      type: type,
      toolbar: {
        show: false,
      },
    },
    colors: [color],
    stroke: {
      curve: "smooth",
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 0 ? `${val}${suffix}` : "";
      },
      style: {
        fontSize: "12px",
      },
      background: {
        enabled: true,
        foreColor: "#000",
        padding: 8,
        borderWidth: 0,
        opacity: 0.9,
      },
    },

    xaxis: {
      type: "datetime",
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      tickAmount: 5,
      labels: {
        formatter: function (value) {
          return `${value.toFixed(0)}${suffix}`;
        },
      },
    },
    grid: {
      borderColor: "#e8e8e8",
    },
    tooltip: {
      y: {
        formatter: function (value) {
          const percentage = ((value / totalUsers) * 100).toFixed(1);
          return `${value}${suffix} ${
            percentage > 0 && totalUsers > 0 ? `(${percentage}%)` : ""
          }`;
        },
        title: {
          formatter: function (seriesName) {
            return seriesName + ": ";
          },
        },
      },
      x: {
        format: isDay ? "dd MM yyyy, hh:mm TT" : "dd MMM yyyy",
      },
    },
  };

  return chartOptions;
};

export const getColorForStrength = (strength) => {
  switch (strength) {
    case 2:
      return "rgb(246, 180, 77)"; // Fair
    case 3:
      return "rgb(8, 162, 98)"; // Good
    case 4:
      return "rgb(8, 162, 98)"; // Strong
    default:
      return "rgb(239, 72, 54)"; // Weak
  }
};

export const getStrengthLabel = (strength) => {
  switch (strength) {
    case 2:
      return "Fair";
    case 3:
      return "Good";
    case 4:
      return "Strong";
    default:
      return "Weak";
  }
};

export const positiveStyle = { color: "rgb(8, 162, 98)" }; // Positive indicator color
export const negativeStyle = { color: "rgb(239, 72, 54)" };

// CONVERT BYTES TO KB, MB, GB, TB, PB, EB, ZB, YB
export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

// UPLOAD DOCUMENT VALIDATION
export const documentValidation = (
  file,
  checkExtension = true,
  setError = (error) => toast.error(error),
) => {
  let res = true;
  const isDoc =
    /(\.doc|\.docx|\.odt|\.pdf|\.tex|\.txt|\.rtf|\.wps|\.wks|\.wpd|\.ppt|\.pptx|\.xls|\.xlsx)$/i.test(
      file.name,
    );
  if (checkExtension && !isDoc) {
    setError("You can only upload doc file!");
    res = false;
  }
  const isLt5M = file.size / 1024 / 1024 < 50;
  if (!isLt5M) {
    res = false;
    setError(`${file.name} must be smaller than 50MB!`);
  }

  return res;
};

// UPLOAD ATTACHMENT VALIDATION
export const attachmentValidation = (file, setError = (error) => toast.error(error)) => {
  let res = true;
  const isLt5M = file.size / 1024 / 1024 < 50;
  if (!isLt5M) {
    res = false;
    setError(`${file.name} must be smaller than 50MB!`);
  }

  return res;
};

export const getInitials = (name = "") => {
  const names = name.split(" ");
  return names
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

export const addHttpsPrefix = (url) => {
  if (!url) return "";
  const trimmedUrl = url.trim();
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
};

function generateCodeVerifier(length = 128) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/[^a-zA-Z0-9]/g, "")
    .substring(0, 128);
}

async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export const salesforceOauth = async (redirectTo = "/dataset") => {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  // Save the verifier temporarily in sessionStorage
  sessionStorage.setItem("salesforce_code_verifier", verifier);
  sessionStorage.setItem("oauth-redirect", redirectTo);

  const clientId =
    "3MVG9GIRg253.lotUYgkDWfUapy53_8bHwWn1XHhq2JUUk66AigdyO.92CY5AKxRBDRNXqOQ1fKn91sNsHQyR";
  const redirectUri = `http://localhost:3000/leads/oauth/callback`; // frontend callback
  const authUrl = `https://login.salesforce.com/services/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&code_challenge=${challenge}&code_challenge_method=S256&scope=refresh_token api`;

  window.location.href = authUrl;
};

export const validateAndNormalizeURL = (url) => {
  if (!url || typeof url !== "string") {
    return {
      isValid: false,
      url: "",
    };
  }

  let normalized = url.trim();

  if (!normalized) {
    return {
      isValid: false,
      url: "",
    };
  }

  // Add https:// if missing and looks like a domain
  if (!/^https?:\/\//i.test(normalized) && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(normalized)) {
    normalized = "https://" + normalized;
  }

  // Validate only https URLs
  const re = /^https:\/\/[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,6}(:[0-9]{1,5})?(\/.*)?$/i;

  const isValid = re.test(normalized);

  return {
    isValid,
    url: isValid ? normalized : url, // keep original if invalid
  };
};

export const formatSearchParams = (params) => {
  if (params == null) return [];
  if (typeof params === "string") {
    try {
      params = JSON.parse(params);
    } catch {
      return [{ paramName: "Raw", paramValue: params, _rowKey: "raw" }];
    }
  }
  if (typeof params !== "object" || Array.isArray(params)) return [];
  return Object.entries(params)
    .filter(([k, v]) => v !== undefined && v !== null && v !== "")
    .map(([key, value], i) => ({
      _rowKey: `${key}-${i}`,
      paramName: key.replace(/_filter$/g, "").replace(/_/g, " "),
      paramValue: Array.isArray(value) ? value.join(", ") : String(value),
    }));
};
